import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';
import bindAll from 'lodash.bindall';
import classNames from 'classnames';

import {ComingSoonTooltip} from '../coming-soon/coming-soon.jsx';

import styles from './ml-environment.css';
import appLogo from '../menu-bar/logo.png';
import settingsIcon from '../menu-bar/icon--settings.svg';
import backIcon from '../../lib/assets/icon--back.svg';
import helpIcon from '../../lib/assets/icon--help.svg';
import languageIcon from '../language-selector/language-icon.svg';
import searchIconSvg from '../action-menu/icon--search.svg';
import mlDb from '../../lib/ml/db';
import MlSandbox from './ml-sandbox.jsx';

/* ── Inline SVG icon components ────────────────────────────── */

const ImageIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <rect
            x="3"
            y="3"
            width="18"
            height="18"
            rx="3"
            stroke="currentColor"
            strokeWidth="1.8"
        />
        <circle
            cx="8.5"
            cy="8.5"
            r="2"
            fill="currentColor"
        />
        <path
            d="M3 16l4.5-4.5a1.5 1.5 0 0 1 2.1 0L14 16"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
        />
        <path
            d="M13 14.5l1.5-1.5a1.5 1.5 0 0 1 2.1 0L21 17"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
        />
    </svg>
);

const AudioIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <rect
            x="9"
            y="2"
            width="6"
            height="12"
            rx="3"
            stroke="currentColor"
            strokeWidth="1.8"
        />
        <path
            d="M5 11a7 7 0 0 0 14 0"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
        />
        <line
            x1="12"
            y1="18"
            x2="12"
            y2="22"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
        />
        <line
            x1="9"
            y1="22"
            x2="15"
            y2="22"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
        />
    </svg>
);

const PoseIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <circle
            cx="12"
            cy="4.5"
            r="2.5"
            stroke="currentColor"
            strokeWidth="1.8"
        />
        <line
            x1="12"
            y1="7"
            x2="12"
            y2="15"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
        />
        <line
            x1="12"
            y1="9"
            x2="7"
            y2="13"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
        />
        <line
            x1="12"
            y1="9"
            x2="17"
            y2="13"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
        />
        <line
            x1="12"
            y1="15"
            x2="8"
            y2="21"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
        />
        <line
            x1="12"
            y1="15"
            x2="16"
            y2="21"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
        />
    </svg>
);

const DataIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <rect
            x="3"
            y="14"
            width="4"
            height="7"
            rx="1"
            fill="currentColor"
        />
        <rect
            x="10"
            y="9"
            width="4"
            height="12"
            rx="1"
            fill="currentColor"
        />
        <rect
            x="17"
            y="3"
            width="4"
            height="18"
            rx="1"
            fill="currentColor"
        />
    </svg>
);

const FolderIcon = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M3 7V17a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
        />
    </svg>
);

const PlusIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <line
            x1="8"
            y1="2"
            x2="8"
            y2="14"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
        <line
            x1="2"
            y1="8"
            x2="14"
            y2="8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
    </svg>
);

const TrashIcon = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M3 6h18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
        <path
            d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"
            stroke="currentColor"
            strokeWidth="2"
        />
        <path
            d="M5 6l1 14a2 2 0 002 2h8a2 2 0 002-2l1-14"
            stroke="currentColor"
            strokeWidth="2"
        />
    </svg>
);

const OpenIcon = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
        <path
            d="M15 3h6v6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <line
            x1="10"
            y1="14"
            x2="21"
            y2="3"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
    </svg>
);

const NeuralIcon = () => (
    <svg
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <circle
            cx="16"
            cy="16"
            r="4"
            fill="#855CD6"
            opacity="0.5"
        />
        <circle
            cx="16"
            cy="32"
            r="4"
            fill="#855CD6"
            opacity="0.7"
        />
        <circle
            cx="16"
            cy="48"
            r="4"
            fill="#855CD6"
            opacity="0.5"
        />
        <circle
            cx="32"
            cy="22"
            r="5"
            fill="#855CD6"
            opacity="0.9"
        />
        <circle
            cx="32"
            cy="42"
            r="5"
            fill="#855CD6"
            opacity="0.9"
        />
        <circle
            cx="48"
            cy="32"
            r="6"
            fill="#855CD6"
        />
        <line
            x1="20"
            y1="16"
            x2="27"
            y2="22"
            stroke="#855CD6"
            strokeWidth="1.2"
            opacity="0.4"
        />
        <line
            x1="20"
            y1="32"
            x2="27"
            y2="22"
            stroke="#855CD6"
            strokeWidth="1.2"
            opacity="0.4"
        />
        <line
            x1="20"
            y1="32"
            x2="27"
            y2="42"
            stroke="#855CD6"
            strokeWidth="1.2"
            opacity="0.4"
        />
        <line
            x1="20"
            y1="48"
            x2="27"
            y2="42"
            stroke="#855CD6"
            strokeWidth="1.2"
            opacity="0.4"
        />
        <line
            x1="37"
            y1="22"
            x2="42"
            y2="32"
            stroke="#855CD6"
            strokeWidth="1.2"
            opacity="0.4"
        />
        <line
            x1="37"
            y1="42"
            x2="42"
            y2="32"
            stroke="#855CD6"
            strokeWidth="1.2"
            opacity="0.4"
        />
    </svg>
);

const CloseIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <line
            x1="6"
            y1="6"
            x2="18"
            y2="18"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
        />
        <line
            x1="18"
            y1="6"
            x2="6"
            y2="18"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
        />
    </svg>
);

/**
 * ML Environment — full-page overlay component.
 *
 * Matches PictoBlox "Machine Learning Environment" layout:
 *   Navbar:  [Logo Box] [Title] [File] [Learn]  ...spacer...  [icons] [← Back]
 *   Toolbar: [My Projects] [Search] [Create New Project] [Open ML Project]
 *   Content: Project table with purple header / empty state
 */
class MLEnvironment extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleCreateProject',
            'handleDeleteProject',
            'handleCloseModal',
            'handleOpenModal',
            'handleProjectNameChange',
            'handleProjectTypeChange',
            'loadProjects',
            'handleStopPropagation',
            'handleTypeClick',
            'handleSearchQueryChange',
            'handleDeleteClick',
            'handleOpenProject',
            'handleCloseProject'
        ]);
        this.state = {
            projects: [],
            showNewProjectModal: false,
            newProjectName: '',
            newProjectType: 'image',
            searchQuery: '',
            activeProject: null,
            isShaking: false
        };
    }

    componentDidMount () {
        this.loadProjects();
    }

    loadProjects () {
        mlDb.getAllProjects()
            .then(projects => {
                this.setState({projects});
            })
            .catch(err => {
                console.error('Failed to load ML projects:', err);
            });
    }

    handleStopPropagation (e) {
        e.stopPropagation();
    }

    handleTypeClick (e) {
        const type = e.currentTarget.getAttribute('data-type');
        this.handleProjectTypeChange(type);
    }

    handleSearchQueryChange (e) {
        this.setState({searchQuery: e.target.value});
    }

    handleDeleteClick (e) {
        const id = Number(e.currentTarget.getAttribute('data-id'));
        this.handleDeleteProject(id);
    }

    handleOpenProject (e) {
        const id = Number(e.currentTarget.getAttribute('data-id'));
        const project = this.state.projects.find(p => p.id === id);
        if (project) {
            this.setState({activeProject: project});
        }
    }

    handleCloseProject () {
        this.setState({activeProject: null});
        this.loadProjects();
    }

    handleOpenModal () {
        this.setState({showNewProjectModal: true, newProjectName: '', newProjectType: 'image'});
    }

    handleCloseModal () {
        this.setState({showNewProjectModal: false});
    }

    handleProjectNameChange (e) {
        this.setState({newProjectName: e.target.value});
    }

    handleProjectTypeChange (type) {
        this.setState({newProjectType: type});
    }

    handleCreateProject () {
        if (!this.state.newProjectName.trim()) return;
        const project = {
            id: Date.now(),
            name: this.state.newProjectName.trim(),
            type: this.state.newProjectType,
            classes: 0,
            lastUpdated: Date.now(),
            status: 'draft'
        };
        mlDb.saveProject(project)
            .then(() => {
                this.setState(prev => ({
                    projects: [project, ...prev.projects],
                    showNewProjectModal: false
                }));
            })
            .catch(err => {
                console.error('Failed to save project:', err);
            });
    }

    handleDeleteProject (id) {
        mlDb.deleteProject(id)
            .then(() => {
                this.setState(prev => ({
                    projects: prev.projects.filter(p => p.id !== id)
                }));
            })
            .catch(err => {
                console.error('Failed to delete project:', err);
            });
    }

    getFilteredProjects () {
        const q = this.state.searchQuery.toLowerCase();
        if (!q) return this.state.projects;
        return this.state.projects.filter(p =>
            p.name.toLowerCase().includes(q) ||
            p.type.toLowerCase().includes(q)
        );
    }

    renderTypeIcon (type) {
        switch (type) {
        case 'image': return <ImageIcon />;
        case 'audio': return <AudioIcon />;
        case 'pose': return <PoseIcon />;
        default: return <DataIcon />;
        }
    }

    renderStatusBadge (status) {
        const cls = status === 'ready' ? styles.mlStatusReady :
            status === 'training' ? styles.mlStatusTraining :
                styles.mlStatusDraft;
        return <span className={`${styles.mlStatus} ${cls}`}>{status}</span>;
    }

    renderNewProjectModal () {
        if (!this.state.showNewProjectModal) return null;
        const types = [
            {key: 'image',
                icon: <ImageIcon />,
                label: <FormattedMessage
                    id="gui.mlEnvironment.image"
                    defaultMessage="Image"
                />},
            {key: 'audio',
                icon: <AudioIcon />,
                label: <FormattedMessage
                    id="gui.mlEnvironment.audio"
                    defaultMessage="Audio"
                />},
            {key: 'pose',
                icon: <PoseIcon />,
                label: <FormattedMessage
                    id="gui.mlEnvironment.pose"
                    defaultMessage="Pose"
                />}
        ];
        return (
            <div
                className={styles.mlModalBackdrop}
                onClick={this.handleCloseModal}
            >
                {/* eslint-disable-next-line */}
                <div
                    className={styles.mlModal}
                    onClick={this.handleStopPropagation}
                >
                    <div className={styles.mlModalHeader}>
                        <h3><FormattedMessage
                            id="gui.mlEnvironment.createNewMlProject"
                            defaultMessage="Create New ML Project"
                        /></h3>
                        <button
                            className={styles.mlModalClose}
                            onClick={this.handleCloseModal}
                        ><CloseIcon /></button>
                    </div>
                    <div className={styles.mlModalBody}>
                        <div className={styles.mlFormGroup}>
                            <label><FormattedMessage
                                id="gui.mlEnvironment.projectName"
                                defaultMessage="Project Name"
                            /></label>
                            <input
                                placeholder="My ML Project"
                                type="text"
                                value={this.state.newProjectName}
                                onChange={this.handleProjectNameChange}
                            />
                        </div>
                        <div className={styles.mlFormGroup}>
                            <label><FormattedMessage
                                id="gui.mlEnvironment.classificationType"
                                defaultMessage="Classification Type"
                            /></label>
                            <div className={styles.mlTypeCards}>
                                {types.map(t => (
                                    <div
                                        className={`${styles.mlTypeCard} ${
                                            this.state.newProjectType === t.key ? styles.selected : ''
                                        }`}
                                        data-type={t.key}
                                        key={t.key}
                                        onClick={this.handleTypeClick}
                                    >
                                        <span className={styles.mlTypeCardIcon}>{t.icon}</span>
                                        <span className={styles.mlTypeCardLabel}>{t.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={styles.mlModalFooter}>
                        <button
                            className={`${styles.mlToolbarBtn} ${styles.mlToolbarBtnOutline}`}
                            onClick={this.handleCloseModal}
                        >
                            <FormattedMessage
                                defaultMessage="Cancel"
                                id="gui.mlEnvironment.cancel"
                            />
                        </button>
                        <ComingSoonTooltip
                            place="top"
                            tooltipId="ml-environment-locked"
                            customMessage="ML Environment available in advanced version V3.0 (IoT.Prarambh)"
                        >
                            <button
                                className={classNames(styles.mlToolbarBtn, {
                                    [styles.shake]: this.state.isShaking
                                })}
                                onClick={() => {
                                    this.setState({isShaking: true});
                                    setTimeout(() => this.setState({isShaking: false}), 400);
                                }}
                            >
                                <span style={{marginRight: '6px', fontSize: '1.2em'}}>🔒</span>
                                <FormattedMessage
                                    defaultMessage="Create Project"
                                    id="gui.mlEnvironment.createProject"
                                />
                            </button>
                        </ComingSoonTooltip>
                    </div>
                </div>
            </div>
        );
    }

    render () {
        if (!this.props.isOpen) return null;

        if (this.state.activeProject) {
            return (
                <div className={styles.mlOverlay}>
                    <MlSandbox
                        project={this.state.activeProject}
                        onBack={this.handleCloseProject}
                    />
                </div>
            );
        }

        const filtered = this.getFilteredProjects();

        return (
            <div className={styles.mlOverlay}>
                {/* ═══ Navigation Bar ═══ */}
                <div className={styles.mlNavbar}>
                    {/* Left: logo + title + links */}
                    <div className={styles.mlNavbarLeft}>
                        <div className={styles.mlLogoWrapper}>
                            <img
                                alt="Prarambh"
                                className={styles.mlLogoImg}
                                draggable={false}
                                src={appLogo}
                            />
                        </div>
                        <span className={styles.mlNavbarTitle}>
                            <FormattedMessage
                                id="gui.mlEnvironment.title"
                                defaultMessage="Machine Learning Environment"
                            />
                        </span>
                        <div className={styles.mlNavbarLinks}>
                            <span className={styles.mlNavbarLink}><FormattedMessage
                                id="gui.mlEnvironment.file"
                                defaultMessage="File"
                            /></span>
                            <span className={styles.mlNavbarLink}><FormattedMessage
                                id="gui.mlEnvironment.learn"
                                defaultMessage="Learn"
                            /></span>
                        </div>
                    </div>

                    {/* Spacer */}
                    <div className={styles.mlNavbarSpacer} />

                    {/* Right: icon buttons + Back */}
                    <div className={styles.mlNavbarRight}>
                        <button
                            className={styles.mlNavbarIconBtn}
                            title="Help"
                        >
                            <img
                                alt=""
                                src={helpIcon}
                                style={{width: 18, height: 18, filter: 'brightness(0) invert(1)'}}
                            />
                        </button>
                        <button
                            className={styles.mlNavbarIconBtn}
                            title="Settings"
                        >
                            <img
                                alt=""
                                src={settingsIcon}
                                style={{width: 18, height: 18, filter: 'brightness(0) invert(1)'}}
                            />
                        </button>
                        <button
                            className={styles.mlNavbarIconBtn}
                            title="Language"
                        >
                            <img
                                alt=""
                                src={languageIcon}
                                style={{width: 20, height: 20}}
                            />
                        </button>
                        <div className={styles.mlNavbarSeparator} />
                        <button
                            className={styles.mlBackBtn}
                            onClick={this.props.onClose}
                        >
                            <img
                                alt=""
                                src={backIcon}
                                className={styles.mlBackArrow}
                            />
                            <FormattedMessage
                                id="gui.mlEnvironment.back"
                                defaultMessage="Back"
                            />
                        </button>
                    </div>
                </div>

                {/* ═══ Toolbar ═══ */}
                <div className={styles.mlToolbar}>
                    <span className={styles.mlToolbarTitle}><FormattedMessage
                        id="gui.mlEnvironment.myProjects"
                        defaultMessage="My Projects"
                    /></span>
                    <div className={styles.mlSearch}>
                        <img
                            alt=""
                            className={styles.mlSearchIcon}
                            src={searchIconSvg}
                        />
                        <input
                            placeholder="Search projects..."
                            type="text"
                            value={this.state.searchQuery}
                            onChange={this.handleSearchQueryChange}
                        />
                    </div>
                    <div className={styles.mlToolbarSpacer} />
                    <button
                        className={styles.mlToolbarBtn}
                        onClick={this.handleOpenModal}
                    >
                        <PlusIcon />
                        <FormattedMessage
                            id="gui.mlEnvironment.createNewProject"
                            defaultMessage="Create New Project"
                        />
                    </button>
                    <button
                        className={`${styles.mlToolbarBtn} ${styles.mlToolbarBtnOutline}`}
                    >
                        <FolderIcon />
                        <FormattedMessage
                            id="gui.mlEnvironment.openMlProject"
                            defaultMessage="Open ML Project"
                        />
                    </button>
                </div>

                {/* ═══ Content — project cards / table ═══ */}
                <div className={styles.mlContent}>
                    {filtered.length > 0 ? (
                        <div className={styles.mlProjectGrid}>
                            {filtered.map(p => (
                                <div
                                    className={styles.mlProjectCard}
                                    key={p.id}
                                >
                                    <div className={styles.mlProjectCardHeader}>
                                        <div className={styles.mlProjectTypeIconWrap}>
                                            {this.renderTypeIcon(p.type)}
                                        </div>
                                        <div className={styles.mlProjectInfo}>
                                            <span className={styles.mlProjectName}>{p.name}</span>
                                            <span className={styles.mlProjectMeta}>
                                                {p.type.charAt(0).toUpperCase() + p.type.slice(1)}
                                                <FormattedMessage
                                                    id="gui.mlEnvironment.classification"
                                                    defaultMessage=" Classification"
                                                />
                                            </span>
                                        </div>
                                        {this.renderStatusBadge(p.status || 'draft')}
                                    </div>
                                    <div className={styles.mlProjectCardBody}>
                                        <div className={styles.mlProjectStat}>
                                            <span className={styles.mlProjectStatValue}>
                                                {Array.isArray(p.classes) ? p.classes.length : (p.classes || 0)}
                                            </span>
                                            <span className={styles.mlProjectStatLabel}>
                                                <FormattedMessage
                                                    id="gui.mlEnvironment.classes"
                                                    defaultMessage="Classes"
                                                />
                                            </span>
                                        </div>
                                        <div className={styles.mlProjectStat}>
                                            <span className={styles.mlProjectStatValue}>
                                                {typeof p.lastUpdated === 'number' ?
                                                    new Date(p.lastUpdated).toLocaleDateString() :
                                                    p.lastUpdated}
                                            </span>
                                            <span className={styles.mlProjectStatLabel}>
                                                <FormattedMessage
                                                    id="gui.mlEnvironment.updated"
                                                    defaultMessage="Updated"
                                                />
                                            </span>
                                        </div>
                                    </div>
                                    <div className={styles.mlProjectCardActions}>
                                        <button
                                            className={styles.mlCardBtnPrimary}
                                            data-id={p.id}
                                            onClick={this.handleOpenProject}
                                        >
                                            <OpenIcon />
                                            <FormattedMessage
                                                id="gui.mlEnvironment.open"
                                                defaultMessage="Open"
                                            />
                                        </button>
                                        <button
                                            className={styles.mlCardBtnDanger}
                                            data-id={p.id}
                                            onClick={this.handleDeleteClick}
                                        >
                                            <TrashIcon />
                                            <FormattedMessage
                                                id="gui.mlEnvironment.delete"
                                                defaultMessage="Delete"
                                            />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className={styles.mlEmptyState}>
                            <div className={styles.mlEmptyIllustration}>
                                <div className={styles.mlEmptyOrbits}>
                                    <div className={styles.mlEmptyOrbitIcon}>
                                        <ImageIcon />
                                    </div>
                                    <div className={styles.mlEmptyOrbitIcon}>
                                        <AudioIcon />
                                    </div>
                                    <div className={styles.mlEmptyOrbitIcon}>
                                        <PoseIcon />
                                    </div>
                                    <div className={styles.mlEmptyOrbitIcon}>
                                        <DataIcon />
                                    </div>
                                </div>
                                <NeuralIcon />
                            </div>
                            <p className={styles.mlEmptyTitle}>
                                <FormattedMessage
                                    id="gui.mlEnvironment.noProjects"
                                    defaultMessage="No ML projects yet"
                                />
                            </p>
                            <p className={styles.mlEmptySubtitle}>
                                <FormattedMessage
                                    defaultMessage={
                                        'Create your first machine learning project to ' +
                                        'start training image, audio, or pose classifiers ' +
                                        'for your Scratch projects.'
                                    }
                                    id="gui.mlEnvironment.noProjectsDesc"
                                />
                            </p>
                            <button
                                className={styles.mlEmptyCta}
                                onClick={this.handleOpenModal}
                            >
                                <PlusIcon />
                                <FormattedMessage
                                    id="gui.mlEnvironment.getStarted"
                                    defaultMessage="Get Started"
                                />
                            </button>
                        </div>
                    )}
                </div>

                {this.renderNewProjectModal()}
            </div>
        );
    }
}

MLEnvironment.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
};

export default MLEnvironment;
